<script>
import KanbanAPI from 'dashboard/api/kanban';

export default {
  props: {
    modelValue: {
      type: [Array, Number, String, Object],
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isLoadingFunnels: false,
      isLoadingStages: false,
      availableFunnels: [],
      availableStages: [],
      selectedFunnelId: null,
      selectedStageId: null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.syncFromModelValue();
      },
    },
  },
  mounted() {
    this.fetchFunnels();
  },
  methods: {
    normalizeId(rawValue) {
      const valueAsNumber = Number(rawValue);
      return Number.isInteger(valueAsNumber) && valueAsNumber > 0
        ? valueAsNumber
        : null;
    },
    getSelectedStageIdFromModel() {
      if (Array.isArray(this.modelValue)) {
        return this.normalizeId(this.modelValue[0]);
      }
      return this.normalizeId(this.modelValue);
    },
    sortStages(stages) {
      return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
    },
    async fetchFunnels() {
      this.isLoadingFunnels = true;
      try {
        const { data } = await KanbanAPI.getFunnels();
        this.availableFunnels = Array.isArray(data) ? data : [];
        await this.syncFromModelValue();
      } catch (error) {
        this.availableFunnels = [];
        console.error('Failed to load funnels for automation action:', error);
      } finally {
        this.isLoadingFunnels = false;
      }
    },
    async fetchStagesForFunnel(funnelId, preserveStageSelection = false) {
      const normalizedFunnelId = this.normalizeId(funnelId);
      if (!normalizedFunnelId) {
        this.availableStages = [];
        this.selectedStageId = null;
        return;
      }

      this.isLoadingStages = true;
      try {
        const { data } = await KanbanAPI.getFunnel(normalizedFunnelId);
        this.availableStages = this.sortStages(data?.kanban_stages || []);

        if (preserveStageSelection) {
          const currentStageExists = this.availableStages.some(
            stage => this.normalizeId(stage.id) === this.selectedStageId
          );
          if (!currentStageExists) {
            this.selectedStageId = null;
          }
          return;
        }

        this.selectedStageId = null;
      } catch (error) {
        this.availableStages = [];
        this.selectedStageId = null;
        console.error('Failed to load funnel stages for automation action:', error);
      } finally {
        this.isLoadingStages = false;
      }
    },
    async syncFromModelValue() {
      const selectedStageId = this.getSelectedStageIdFromModel();

      if (!selectedStageId) {
        this.selectedStageId = null;
        return;
      }

      if (
        this.availableStages.some(
          stage => this.normalizeId(stage.id) === selectedStageId
        )
      ) {
        this.selectedStageId = selectedStageId;
        return;
      }

      if (!this.availableFunnels.length) return;

      for (const funnel of this.availableFunnels) {
        const normalizedFunnelId = this.normalizeId(funnel.id);
        if (!normalizedFunnelId) continue;

        try {
          const { data } = await KanbanAPI.getFunnel(normalizedFunnelId);
          const stages = this.sortStages(data?.kanban_stages || []);
          const matchingStage = stages.find(
            stage => this.normalizeId(stage.id) === selectedStageId
          );

          if (matchingStage) {
            this.selectedFunnelId = normalizedFunnelId;
            this.availableStages = stages;
            this.selectedStageId = selectedStageId;
            return;
          }
        } catch (error) {
          console.error('Failed to map stage to funnel in automation action:', error);
        }
      }
    },
    async onFunnelChange() {
      await this.fetchStagesForFunnel(this.selectedFunnelId, false);
      this.emitStageSelection();
    },
    emitStageSelection() {
      const normalizedStageId = this.normalizeId(this.selectedStageId);
      const payload = normalizedStageId ? [normalizedStageId] : [];
      this.$emit('update:modelValue', payload);
    },
  },
};
</script>

<template>
  <div class="kanban-stage-selector">
    <label class="block text-xs font-medium text-n-slate-12 mb-1">
      {{ $t('AUTOMATION.ACTION.KANBAN_FUNNEL_LABEL') }}
    </label>
    <select
      v-model="selectedFunnelId"
      class="mb-2"
      :disabled="isLoadingFunnels"
      @change="onFunnelChange"
    >
      <option :value="null">
        {{ $t('AUTOMATION.ACTION.KANBAN_FUNNEL_INPUT_PLACEHOLDER') }}
      </option>
      <option
        v-for="funnel in availableFunnels"
        :key="funnel.id"
        :value="funnel.id"
      >
        {{ funnel.name }}
      </option>
    </select>

    <label class="block text-xs font-medium text-n-slate-12 mb-1">
      {{ $t('AUTOMATION.ACTION.KANBAN_STAGE_LABEL') }}
    </label>
    <select
      v-model="selectedStageId"
      :disabled="!selectedFunnelId || isLoadingStages"
      @change="emitStageSelection"
    >
      <option :value="null">
        {{ $t('AUTOMATION.ACTION.KANBAN_STAGE_INPUT_PLACEHOLDER') }}
      </option>
      <option
        v-for="stage in availableStages"
        :key="stage.id"
        :value="stage.id"
      >
        {{ stage.name }}
      </option>
    </select>
  </div>
</template>
